import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { RefreshCw, Search } from "lucide-react";

import { api, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtDate, relTime } from "@/pages/admin/dates";

// Human-friendly labels + tone for each action name emitted by the backend.
// Anything unknown falls back to the raw action string in a neutral tone.
const ACTION_META = {
  waitlist_update: { label: "Row updated", tone: "bg-[#EDEAE4] text-[#2A2724]" },
  waitlist_bulk_update: { label: "Bulk update", tone: "bg-gold/15 text-[#8B6F1F]" },
  waitlist_delete: { label: "Row deleted", tone: "bg-[#F8ECEC] text-[#a13a3a]" },
  waitlist_bulk_delete: { label: "Bulk delete", tone: "bg-[#F8ECEC] text-[#a13a3a]" },
  terms_signed: { label: "Terms signed", tone: "bg-[#E9F5EC] text-[#2f7d3f]" },
  cohort_2_bulk_promote: { label: "Cohort 2 promoted", tone: "bg-gold/20 text-[#8B6F1F]" },
  pricing_lead_delete: { label: "Pricing lead removed", tone: "bg-[#F8ECEC] text-[#a13a3a]" },
};

export default function AdminAuditLog() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/audit-log?limit=100");
      setRows(data?.actions || []);
    } catch (e) {
      setError("Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    load();
  }, [user]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.trim().toLowerCase();
    return rows.filter((r) =>
      [
        r.actor,
        r.action,
        JSON.stringify(r.target_ids || []),
        JSON.stringify(r.payload || {}),
      ]
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [rows, q]);

  // useAuth returns null while it's still fetching /api/auth/me on first
  // load — render a placeholder for that beat so we don't flash-redirect
  // signed-in admins to /login when the cookie is still being validated.
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace state={{ from: "/admin/audit-log" }} />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#F7F5F2] px-6 py-10 text-[#2A2724]">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-[#8B6F1F]">
              Admin
            </p>
            <h1 className="mt-1 font-serif text-4xl font-light">Audit log</h1>
            <p className="mt-1 text-sm text-[#6B6B6B]">
              Last {rows.length} admin actions · newest first · read-only
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9a9a9a]" />
              <Input
                data-testid="audit-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Filter by actor / action / id"
                className="w-64 rounded-full pl-9"
              />
            </div>
            <Button
              data-testid="audit-refresh"
              onClick={load}
              variant="outline"
              className="rounded-full border-black/10"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>

        {loading && (
          <p className="rounded-2xl border border-black/10 bg-white p-6 text-sm text-[#6B6B6B]">
            Loading…
          </p>
        )}
        {error && (
          <p
            data-testid="audit-error"
            className="rounded-2xl border border-[#a13a3a]/30 bg-[#F8ECEC] p-6 text-sm text-[#a13a3a]"
          >
            {error}
          </p>
        )}
        {!loading && !error && filtered.length === 0 && (
          <p
            data-testid="audit-empty"
            className="rounded-2xl border border-black/10 bg-white p-6 text-sm text-[#6B6B6B]"
          >
            No admin actions match your filter yet.
          </p>
        )}
        {!loading && !error && filtered.length > 0 && (
          <div
            data-testid="audit-table"
            className="overflow-hidden rounded-2xl border border-black/10 bg-white"
          >
            <table className="w-full text-sm">
              <thead className="bg-[#EDEAE4] text-left text-xs uppercase tracking-[0.14em] text-[#6B6B6B]">
                <tr>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Actor</th>
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3">Targets</th>
                  <th className="px-4 py-3">Payload</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const meta = ACTION_META[r.action] || {
                    label: r.action,
                    tone: "bg-[#F7F5F2] text-[#2A2724]",
                  };
                  const targets = r.target_ids || [];
                  return (
                    <tr
                      key={r._id}
                      data-testid={`audit-row-${r._id}`}
                      className="border-t border-black/5 align-top hover:bg-[#F7F5F2]"
                    >
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="text-[#2A2724]">{fmtDate(r.created_at)}</div>
                        <div className="text-[0.68rem] uppercase tracking-[0.14em] text-[#9a9a9a]">
                          {relTime(r.created_at)}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 font-medium text-[#2A2724]">
                        {r.actor || "—"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[0.65rem] font-medium uppercase tracking-[0.14em] ${meta.tone}`}
                        >
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#6B6B6B]">
                        {targets.length === 0 ? (
                          <span className="text-[#9a9a9a]">—</span>
                        ) : (
                          <span title={targets.join(", ")}>
                            {targets.length === 1
                              ? targets[0].slice(0, 8) + "…"
                              : `${targets.length} rows`}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-[0.7rem] text-[#6B6B6B]">
                        <PayloadCell payload={r.payload} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Compact JSON preview that expands on click — keeps the row scannable while
// still letting Kate/Bella drill into anything unusual without leaving the
// page.
function PayloadCell({ payload }) {
  const [open, setOpen] = useState(false);
  if (!payload || Object.keys(payload).length === 0)
    return <span className="text-[#9a9a9a]">—</span>;
  const raw = JSON.stringify(payload);
  return (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      className="max-w-md rounded border border-black/10 bg-[#F7F5F2] px-2 py-1 text-left leading-tight hover:border-black/30"
    >
      {open ? JSON.stringify(payload, null, 2) : raw.slice(0, 90) + (raw.length > 90 ? "…" : "")}
    </button>
  );
}
